import { addExceptionNotification, addSuccess } from '@mse-reg/app/modules/notification.slice';
import {
  CommentDto,
  FileImportDto,
  FileResponse,
  IImportClient,
  IRegistrationEditClient,
  ModuleRegistrationInfoDto,
  PhaseDto,
  PhaseType,
  RegistrationValidationDto,
  resolver,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import {
  adjustSemesterFilter,
  matchSemesterFilter,
  SemesterFilterValues,
} from '../selections/semester-selection.slice';

// Types
type ThunkFn<T = void> = (dispatch: any, getState: () => any) => Promise<T>;

// State
interface ImportsState {
  loading: boolean;
  errorMessage: string | null;
  imports: FileImportDto[];
  selectedResult: RegistrationValidationDto | null;
  selectedImportId: number;
  selectedPhase: PhaseDto | null | undefined;
  mainContentLoading: boolean;
  previewLoading: boolean;
  selectedRegistration: ModuleRegistrationInfoDto | null;
  moduleRegistrationLoading: boolean;
}

const initialState: ImportsState = {
  loading: false,
  errorMessage: null,
  imports: [],
  selectedResult: null,
  selectedImportId: 0,
  selectedPhase: undefined as PhaseDto | null | undefined,
  mainContentLoading: true,
  previewLoading: false,
  selectedRegistration: null,
  moduleRegistrationLoading: false,
};

// Slice
const importsSlice = createSlice({
  name: 'registration-import/imports',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
      if (action.payload) state.errorMessage = null;
    },
    setImports(state, action: PayloadAction<FileImportDto[]>) {
      state.loading = false;
      state.imports = action.payload;
    },
    setImportsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.errorMessage = action.payload;
    },
    setPreviewLoading(state, action: PayloadAction<boolean>) {
      state.previewLoading = action.payload;
      if (action.payload) state.errorMessage = null;
    },
    setSelectedResult(state, action: PayloadAction<RegistrationValidationDto | null>) {
      state.previewLoading = false;
      state.selectedResult = action.payload;
      state.selectedImportId = action.payload?.fileImport?.id ?? 0;
    },
    setValidationError(state, action: PayloadAction<string>) {
      state.previewLoading = false;
      state.errorMessage = action.payload;
    },
    selectImport(state, action: PayloadAction<number | null>) {
      state.selectedImportId = action.payload ?? 0;
      state.selectedResult = null;
    },
    selectPhase(state, action: PayloadAction<PhaseDto | null | undefined>) {
      state.selectedPhase = action.payload;
    },
    setMainContentLoading(state, action: PayloadAction<boolean>) {
      state.mainContentLoading = action.payload;
    },
    setModuleRegistrationLoading(state, action: PayloadAction<boolean>) {
      state.moduleRegistrationLoading = action.payload;
      if (action.payload) state.errorMessage = null;
    },
    setSelectedRegistration(state, action: PayloadAction<ModuleRegistrationInfoDto | null>) {
      state.moduleRegistrationLoading = false;
      state.selectedRegistration = action.payload;
    },
    clearModuleRegistration(state) {
      state.moduleRegistrationLoading = false;
      state.selectedRegistration = null;
    },
  },
});

export const importsReducer = importsSlice.reducer;
export const importsActions = importsSlice.actions;
export type { ImportsState };

// Actions factory
const initActions = (importClient: IImportClient, registrationEditClient: IRegistrationEditClient) => {
  const { actions } = importsSlice;

  const selectImportAsync: (id?: number) => ThunkFn<RegistrationValidationDto | null> =
    (id) => async (dispatch, getState) => {
      if (id && id > 0) {
        dispatch(actions.selectImport(id));
        dispatch(actions.setPreviewLoading(true));

        try {
          const result = await importClient.getImport(id);
          dispatch(actions.setSelectedResult(result));

          const imported = result?.registrationImport;
          const phase = getState().importsManagement.selectedPhase;
          if (
            (imported?.semesterId && imported.semesterId !== getState().semesterSelection?.selectedSemester?.id) ||
            (phase && phase.id !== result.phaseId)
          ) {
            await dispatch(
              setFilterAsync({
                semesterId: imported.semesterId,
                phaseId: phase && phase.id !== result.phaseId ? result.phaseId : phase?.id,
              }),
            );
          }

          return result;
        } catch (e: any) {
          dispatch(actions.setValidationError(e?.message ?? 'Failed to load import'));
          return null;
        }
      }

      dispatch(actions.setSelectedResult(null));
      return null;
    };

  const getImportsAsync: (filter: SemesterFilterValues) => ThunkFn<FileImportDto[]> = (filter) => async (dispatch) => {
    dispatch(actions.setMainContentLoading(true));
    dispatch(actions.setLoading(true));
    const correctedFilter = dispatch(matchSemesterFilter({ ...filter, priority: 'phase' }));
    const effectivePhase = correctedFilter?.phase?.phaseType === PhaseType.EXAM_REGISTRATION ? null : (correctedFilter?.phase ?? null);
    dispatch(actions.selectPhase(effectivePhase));

    try {
      const imports = await importClient.getImports(
        correctedFilter.academicYear?.id,
        correctedFilter.semester?.id,
        effectivePhase?.id ?? 0,
      );
      dispatch(actions.setImports(imports));
      dispatch(actions.setMainContentLoading(false));
      return imports;
    } catch (e: any) {
      dispatch(actions.setImportsError(e?.message ?? 'Failed to load imports'));
      dispatch(actions.setMainContentLoading(false));
      return [];
    }
  };

  const downloadAsync: (id: number) => ThunkFn<FileResponse | null> = (id) => async (dispatch) => {
    dispatch(actions.setPreviewLoading(true));

    try {
      const fileResponse = await importClient.getOriginalFile(id);
      saveAs(fileResponse.data, fileResponse.fileName);
      dispatch(actions.setPreviewLoading(false));
      return fileResponse;
    } catch (e: any) {
      dispatch(actions.setValidationError(e?.message ?? 'Failed to download file'));
      return null;
    }
  };

  const rejectAsync: (id: number) => ThunkFn<boolean> = (id) => async (dispatch, getState) => {
    dispatch(actions.setPreviewLoading(true));

    try {
      await importClient.reject(id);
      dispatch(actions.setPreviewLoading(false));

      const phase = getState().importsManagement.selectedPhase;
      const semester = getState().semesterSelection.selectedSemester;
      await dispatch(getImportsAsync({ phase, semester }));
      return true;
    } catch (e: any) {
      dispatch(actions.setPreviewLoading(false));
      dispatch(addExceptionNotification(e, 'Failed to reject import.'));
      return false;
    }
  };

  const deleteAsync: (id: number) => ThunkFn<boolean> = (id) => async (dispatch, getState) => {
    dispatch(actions.setPreviewLoading(true));

    try {
      await importClient.delete(id);
      dispatch(actions.setPreviewLoading(false));
      dispatch(actions.selectImport(null));

      const phase = getState().importsManagement.selectedPhase;
      const semester = getState().semesterSelection.selectedSemester;
      await dispatch(getImportsAsync({ phase, semester }));
      return true;
    } catch (e: any) {
      dispatch(actions.setPreviewLoading(false));
      dispatch(addExceptionNotification(e, 'Failed to delete import.'));
      return false;
    }
  };

  const withdrawAsync: (id: number) => ThunkFn<boolean> = (id) => async (dispatch, getState) => {
    dispatch(actions.setPreviewLoading(true));

    try {
      await importClient.withdraw(id);
      dispatch(actions.setPreviewLoading(false));
      dispatch(actions.selectImport(null));

      const phase = getState().importsManagement.selectedPhase;
      const semester = getState().semesterSelection.selectedSemester;
      await dispatch(getImportsAsync({ phase, semester }));
      return true;
    } catch (e: any) {
      dispatch(actions.setPreviewLoading(false));
      dispatch(addExceptionNotification(e, 'Failed to withdraw import.'));
      return false;
    }
  };

  const moveToPhaseAsync: (id: number, phaseId: number) => ThunkFn<boolean> =
    (id, phaseId) => async (dispatch, getState) => {
      dispatch(actions.setPreviewLoading(true));

      try {
        await importClient.moveToPhase(id, phaseId);
        dispatch(actions.setPreviewLoading(false));

        const phase = getState().importsManagement.selectedPhase;
        const semester = getState().semesterSelection.selectedSemester;
        await dispatch(getImportsAsync({ phase, semester }));
        dispatch(actions.selectImport(null));
        return true;
      } catch (e: any) {
        dispatch(actions.setPreviewLoading(false));
        dispatch(addExceptionNotification(e, 'Failed to move import to phase.'));
        return false;
      }
    };

  const setFilterAsync: (filter: SemesterFilterValues) => ThunkFn<void> = (filter) => async (dispatch, getState) => {
    const { selectedAcademicYear, selectedSemester } = getState().semesterSelection;
    let effectiveFilter: SemesterFilterValues;
    if (filter.priority === 'academicYear') {
      effectiveFilter = { academicYear: filter.academicYear, semester: undefined, phase: undefined, allowEmpty: 'phase', priority: 'academicYear' };
    } else if (filter.priority === 'semester') {
      effectiveFilter = { academicYear: selectedAcademicYear, semester: filter.semester, phase: filter.semester ? filter.phase : undefined, allowEmpty: 'phase', priority: 'semester' };
    } else {
      effectiveFilter = { academicYear: selectedAcademicYear, semester: selectedSemester, phase: filter.phase, allowEmpty: 'phase', priority: 'phase' };
    }
    const result = dispatch(adjustSemesterFilter(effectiveFilter, 'phase'));
    dispatch(actions.selectPhase(result.phase ?? null));
    if (
      getState().importsManagement.selectedResult?.fileImport?.semesterId !==
      getState().semesterSelection.selectedSemester?.id
    ) {
      dispatch(actions.setSelectedResult(null));
    }
    await dispatch(getImportsAsync({ ...result, allowEmpty: effectiveFilter.allowEmpty }));
  };

  const addCommentAsync: (comment: CommentDto) => ThunkFn = (comment) => async (dispatch) => {
    try {
      await registrationEditClient.addComment(comment);
      dispatch(actions.setModuleRegistrationLoading(true));
      const registration = await registrationEditClient.getModuleRegistration(comment.registrationId);
      dispatch(actions.setSelectedRegistration(registration));
      dispatch(addSuccess(`Successfully added comment.`));
    } catch (e) {
      dispatch(addExceptionNotification(e, 'An unknown error occurred while saving comment.'));
    }
  };

  const removeCommentAsync: (comment: CommentDto) => ThunkFn = (comment) => async (dispatch) => {
    try {
      await registrationEditClient.deleteComment(comment.id);
      dispatch(actions.setModuleRegistrationLoading(true));
      const registration = await registrationEditClient.getModuleRegistration(comment.registrationId);
      dispatch(actions.setSelectedRegistration(registration));
      dispatch(addSuccess(`Successfully deleted comment.`));
    } catch (e) {
      dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting comment.'));
    }
  };

  const loadModuleRegistrationAsync: (registrationId: number) => ThunkFn =
    (registrationId) => async (dispatch, getState) => {
      if (!registrationId) {
        const registration = getState().importsManagement.selectedRegistration;
        if (registration && !getState().importsManagement.moduleRegistrationLoading) {
          dispatch(actions.clearModuleRegistration());
        }
        return;
      }

      dispatch(actions.setModuleRegistrationLoading(true));
      try {
        const registration = await registrationEditClient.getModuleRegistration(registrationId);
        dispatch(actions.setSelectedRegistration(registration));
      } catch (e) {
        dispatch(addExceptionNotification(e, 'Failed to load module registration.'));
        dispatch(actions.setModuleRegistrationLoading(false));
      }
    };

  return Object.freeze({
    loadModuleRegistrationAsync,
    addCommentAsync,
    removeCommentAsync,
    selectImportAsync,
    getImportsAsync,
    downloadAsync,
    rejectAsync,
    withdrawAsync,
    moveToPhaseAsync,
    deleteAsync,
    setFilterAsync,
  });
};

export default initActions;

export type RegistrationImportActions = ReturnType<typeof initActions>;

export const importsModule = {
  initActions: () =>
    initActions(
      resolver.resolve<IImportClient>('IImportClient'),
      resolver.resolve<IRegistrationEditClient>('IRegistrationEditClient'),
    ),
  reducer: importsReducer,
};
